import React from 'react';
import { mergeProps, useField } from 'react-aria';

import type { ToggleInputProps } from '../../components/ToggleInput/ToggleInput';
import type { BaseFieldProps } from './common/types';
import { Alert } from '../../components/Alert/Alert';
import { ToggleInput } from '../../components/ToggleInput/ToggleInput';
import { Tooltip } from '../../components/Tooltip/Tooltip';
import FeatureBadge from '../../formatting/FeatureBadge/FeatureBadge';
import { styled } from '../../stitches.config';
import { HStack } from '../../utilities/Stack/HStack';
import { VStack } from '../../utilities/Stack/VStack';
import filterErrorMessage from '../../utils/filterErrorMessage';
import { FieldAnchorTarget, useFieldAnchor } from './common/FieldAnchor';
import { FieldDivider, fieldDividerDisplay } from './common/FieldDivider';
import { SecondaryFieldDescription, SecondaryFieldLabel } from './common/FieldLabel';
import { useCustomFieldProps } from './createCustomFieldPropsProvider';
import { MaybeFieldContainer } from './FieldContainer';
import { FieldErrorMessages } from './FieldError';

const SecondaryToggleFieldParent = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  width: '100%',
  gap: '$4',
});
SecondaryToggleFieldParent.displayName = 'SecondaryToggleFieldParent';

const SecondaryToggleFieldLabelling = styled('div', {
  position: 'relative',
  display: 'flex',
  flexDirection: 'row',
  gap: '$6',
});
SecondaryToggleFieldLabelling.displayName = 'SecondaryToggleFieldLabelling';

const SecondaryToggleFieldStart = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '$2',
  width: '100%',
});
SecondaryToggleFieldStart.displayName = 'SecondaryToggleFieldStart';

const SecondaryToggleFieldEnd = styled(HStack, {
  maxHeight: '$20',
});
SecondaryToggleFieldEnd.displayName = 'SecondaryToggleFieldEnd';

const SecondaryToggleFieldAlert = styled(Alert, {
  padding: '$8 $16',
});
SecondaryToggleFieldAlert.displayName = 'SecondaryToggleFieldAlert';

const SecondaryToggleFieldContainer = styled('div', FieldAnchorTarget, {
  zIndex: 1,
  display: 'flex',
  flexDirection: 'column',
  padding: '$10 $16',
  [fieldDividerDisplay]: 'none',

  '&:not(:last-child)': {
    paddingBottom: 0,
    [fieldDividerDisplay]: 'flex',
  },
});
SecondaryToggleFieldContainer.displayName = 'SecondaryToggleFieldContainer';

export interface SecondaryToggleFieldProps extends BaseFieldProps, ToggleInputProps {
  align?: 'center' | 'justify' | 'left' | 'right';
  tooltip?: React.ReactNode;
  /**
   * If true, the toggle will be set to an indeterminate state if the `selected` prop is undefined,
   * and the user can click back to it after changing the value.
   */
  supportsIndeterminate?: boolean;
  children?: React.ReactNode;
  /* Controls that appear after the toggle field input */
  inputStart?: React.ReactNode;
  /* Controls that appear after the toggle field input */
  inputEnd?: React.ReactNode;
}

export function SecondaryToggleField(props: SecondaryToggleFieldProps) {
  const { fieldProps: customFieldProps, inputProps: customInputProps } = useCustomFieldProps();
  const anchor = useFieldAnchor(customFieldProps.name);

  const {
    label = customFieldProps.label,
    errorMessage = customFieldProps.errorMessage,
    description,
    supportsIndeterminate = false,
    internal,
    children,
    tooltip,
    inputStart,
    inputEnd,
    ...extraInputProps
  } = props;

  const {
    labelProps,
    fieldProps: inputProps,
    descriptionProps,
    errorMessageProps,
  } = useField({
    label,
    description,
    errorMessage,
  });

  const indeterminate =
    supportsIndeterminate &&
    'selected' in customInputProps &&
    customInputProps.selected === undefined;

  const input = (
    <MaybeFieldContainer>
      <SecondaryToggleFieldContainer {...anchor.containerProps}>
        <VStack spacing={6}>
          <SecondaryToggleFieldParent>
            <SecondaryToggleFieldStart>
              <SecondaryToggleFieldLabelling>
                <SecondaryFieldLabel {...labelProps}>{label}</SecondaryFieldLabel>
                {internal && <FeatureBadge type="internal" size="x-small" />}
                {anchor.link}
              </SecondaryToggleFieldLabelling>
              {description && (
                <SecondaryFieldDescription {...descriptionProps}>
                  {description}
                </SecondaryFieldDescription>
              )}
            </SecondaryToggleFieldStart>
            <SecondaryToggleFieldEnd spacing={4}>
              {inputStart}
              <ToggleInput
                aria-label={`${label}`}
                size="small"
                canResetToIndeterminate={supportsIndeterminate}
                indeterminate={indeterminate}
                {...mergeProps(customInputProps, inputProps, extraInputProps)}
              />
              {inputEnd}
            </SecondaryToggleFieldEnd>
          </SecondaryToggleFieldParent>
          {children}
        </VStack>
        <FieldDivider context="secondary" />
      </SecondaryToggleFieldContainer>
      {errorMessage && (
        <FieldErrorMessages>
          <SecondaryToggleFieldAlert
            {...errorMessageProps}
            type="inline"
            relation="stacked"
            variant="negative"
            heading={filterErrorMessage(errorMessage)}
          />
        </FieldErrorMessages>
      )}
    </MaybeFieldContainer>
  );
  return tooltip ? (
    <Tooltip contents={tooltip} asChild={false}>
      {input}
    </Tooltip>
  ) : (
    input
  );
}
